import { escapeHtml } from './dom.js';
import { placePopover, popoverHost } from './popover.js';

/**
 * A date field that does not depend on the browser's own.
 *
 * `<input type="date">` is a different control in every engine. Chrome gives
 * a segmented field and a calendar; Safari gives a text box and a stepper,
 * with no way to see a month at all. A ledger is typed in date-first and the
 * date is the field people correct most, so "which browser are you in"
 * deciding how hard that is was never acceptable.
 *
 * So: an ordinary text input holding ISO, plus a calendar of our own. Two
 * consequences worth stating.
 *
 * The value is ISO `YYYY-MM-DD` in the input itself, not in a hidden twin.
 * Every caller already reads `.value` expecting exactly that, and a twin is
 * one more thing that can disagree with what is on screen.
 *
 * And typing stays first. The calendar is for finding a date you do not know
 * ("the Tuesday before last"); typing is for a date you do, which is most of
 * them. So the input accepts what people actually type — `2026-09-29`,
 * `29.09.2026`, `29/9/26`, `29 Sep 2026` — and normalises on blur rather
 * than fighting each keystroke.
 */

const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const DMY = /^(\d{1,2})[./\s-](\d{1,2})[./\s-](\d{2,4})$/;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Monday-first, because that is what every calendar this ledger's readers use looks like. */
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function isoOf(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Today, as the person entering it would write it — their own calendar day.
 *
 * Not `new Date().toISOString().slice(0, 10)`, which is the UTC date: in
 * Budapest that is still yesterday until one or two in the morning, and a
 * new entry defaulted to it. Reported from use, at ten past one.
 */
export function todayIso(): string {
  return isoOf(new Date());
}

/**
 * What the reader typed, as ISO — or undefined if it is not a date.
 *
 * Deliberately lenient about separators and generous about a two-digit year,
 * which is read as this century: a ledger being typed into now is not about
 * 1926, and refusing `29/9/26` to avoid a theoretical ambiguity would reject
 * the single most common shorthand.
 */
export function parseDateInput(raw: string, today = new Date()): string | undefined {
  const text = raw.trim();
  if (text === '') return undefined;

  const iso = ISO.exec(text);
  if (iso) return validate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const dmy = DMY.exec(text);
  if (dmy) {
    const year = Number(dmy[3]);
    return validate(year < 100 ? 2000 + year : year, Number(dmy[2]), Number(dmy[1]));
  }

  // "29 Sep 2026", "29 September", "Sep 29" — the month by name, in either
  // order, with the year optional and this year assumed.
  const words = text.split(/[\s,]+/).filter(Boolean);
  const monthWord = words.findIndex((w) => monthIndexOf(w) >= 0);
  if (monthWord >= 0) {
    const month = monthIndexOf(words[monthWord]!) + 1;
    const numbers = words.filter((_, i) => i !== monthWord).map(Number).filter((n) => Number.isFinite(n));
    const day = numbers.find((n) => n >= 1 && n <= 31);
    const year = numbers.find((n) => n > 31);
    if (day !== undefined) {
      return validate(year === undefined ? today.getFullYear() : year < 100 ? 2000 + year : year, month, day);
    }
  }
  return undefined;
}

function monthIndexOf(word: string): number {
  const lower = word.toLowerCase();
  if (lower.length < 3) return -1;
  return MONTHS.findIndex((m) => m.toLowerCase().startsWith(lower.slice(0, 3)));
}

/**
 * Real dates only. `new Date(2026, 1, 31)` is the 3rd of March, and silently
 * turning somebody's typo into a different date is worse than refusing it.
 */
function validate(year: number, month: number, day: number): string | undefined {
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return undefined;
  return isoOf(date);
}

/** How the value is shown: ISO, which sorts, never surprises and matches what is stored. */
export function dateFieldHtml(
  id: string, label = 'Date', opts: { name?: string; required?: boolean } = {},
): string {
  return `<span class="wl-date">`
    + `<input id="${escapeHtml(id)}" class="wl-nf__ctl wl-date__text" type="text"`
    + (opts.name ? ` name="${escapeHtml(opts.name)}"` : '')
    + (opts.required ? ' required' : '')
    + ` inputmode="numeric" autocomplete="off" spellcheck="false" placeholder="YYYY-MM-DD"`
    + ` aria-label="${escapeHtml(label)}">`
    + `<button type="button" class="wl-date__btn" data-date-open="${escapeHtml(id)}"`
    + ` aria-label="Choose ${escapeHtml(label.toLowerCase())} from a calendar" aria-haspopup="dialog"`
    + ` aria-expanded="false">${CALENDAR_ICON}</button>`
    + `</span>`;
}

const CALENDAR_ICON = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">'
  + '<rect x="1.5" y="3" width="13" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>'
  + '<path d="M1.5 6.5h13" stroke="currentColor" stroke-width="1.3"/>'
  + '<path d="M5 1.5v3M11 1.5v3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>'
  + '</svg>';

export interface MountedDateField { dispose: () => void }

/**
 * Wire a date field: typing, normalising, and the calendar.
 *
 * One calendar element per mount rather than one shared across the page —
 * these are cheap, and a shared one would have to be re-parented every time
 * it opened inside a different dialog to stay visible.
 */
export function mountDateField(
  input: HTMLInputElement, button: HTMLElement, onChange?: (iso: string) => void,
): MountedDateField {
  let panel: HTMLElement | undefined;
  let cursor = new Date();
  /**
   * Which of the three the panel is showing.
   *
   * The days grid is where it opens and where it ends. The other two exist
   * because reaching March 2019 by pressing "previous month" eighty times is
   * not reaching it — the title is a button that zooms out, and picking a
   * month or a year zooms back in, which is the same gesture the native
   * control has and the reason people already know how to use it.
   */
  let view: 'days' | 'months' | 'years' = 'days';

  const commit = (iso: string): void => {
    input.value = iso;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    onChange?.(iso);
  };

  /**
   * On blur, not on every keystroke. Rewriting "29-" into a date the moment
   * a separator appears fights the person typing the rest of it.
   */
  const onBlur = (): void => {
    const parsed = parseDateInput(input.value);
    if (parsed !== undefined && parsed !== input.value) commit(parsed);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      const parsed = parseDateInput(input.value);
      if (parsed !== undefined) { commit(parsed); event.preventDefault(); }
      return;
    }
    // Up and down step a day, the one thing the native control did well.
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    const base = parseDateInput(input.value) ?? isoOf(new Date());
    const date = fromIso(base);
    date.setDate(date.getDate() + (event.key === 'ArrowUp' ? 1 : -1));
    commit(isoOf(date));
    event.preventDefault();
  };

  function close(): void {
    panel?.remove();
    panel = undefined;
    button.setAttribute('aria-expanded', 'false');
  }

  function open(): void {
    if (panel) { close(); return; }
    cursor = fromIso(parseDateInput(input.value) ?? isoOf(new Date()));
    view = 'days';
    panel = document.createElement('div');
    panel.className = 'wl-cal';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Choose a date');
    popoverHost(button).appendChild(panel);
    render();
    place();
    button.setAttribute('aria-expanded', 'true');
    panel.querySelector<HTMLElement>('[data-cal-day][tabindex="0"]')?.focus();
  }

  function place(): void {
    if (panel) placePopover(button, panel);
  }

  /** The first year of the twelve a years view shows — 2020 for 2026. */
  function decadeStart(year: number): number {
    return year - ((year % 12) + 12) % 12;
  }

  function render(): void {
    if (!panel) return;
    panel.innerHTML = view === 'days' ? daysHtml() : view === 'months' ? monthsHtml() : yearsHtml();
  }

  function headHtml(title: string, titleLabel: string, prev: string, next: string): string {
    return `
      <div class="wl-cal__head">
        <button type="button" class="wl-cal__nav" data-cal-prev aria-label="${escapeHtml(prev)}">&#8249;</button>
        <button type="button" class="wl-cal__title" data-cal-zoom aria-label="${escapeHtml(titleLabel)}">
          ${escapeHtml(title)}<span class="wl-cal__caret cf-chev" aria-hidden="true"></span>
        </button>
        <button type="button" class="wl-cal__nav" data-cal-next aria-label="${escapeHtml(next)}">&#8250;</button>
      </div>`;
  }

  function daysHtml(): string {
    const selected = parseDateInput(input.value);
    const todayIso = isoOf(new Date());
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    // Monday-first: getDay() is Sunday-first, so Sunday's 0 becomes 6.
    const lead = (new Date(year, month, 1).getDay() + 6) % 7;
    const days = new Date(year, month + 1, 0).getDate();

    let grid = '';
    for (let i = 0; i < lead; i += 1) grid += '<span class="wl-cal__pad"></span>';
    for (let day = 1; day <= days; day += 1) {
      const iso = isoOf(new Date(year, month, day));
      const classes = ['wl-cal__day'];
      if (iso === selected) classes.push('is-selected');
      if (iso === todayIso) classes.push('is-today');
      // One tab stop for the whole grid, arrows move within it — a roving
      // tabindex, so Tab leaves the calendar rather than walking 31 buttons.
      const focusable = iso === (selected ?? todayIso);
      grid += `<button type="button" class="${classes.join(' ')}" data-cal-day="${iso}"`
        + ` tabindex="${focusable ? 0 : -1}"${iso === selected ? ' aria-current="date"' : ''}>${day}</button>`;
    }
    if (!grid.includes('tabindex="0"')) {
      grid = grid.replace('tabindex="-1"', 'tabindex="0"');
    }

    return headHtml(`${MONTHS[month]!} ${year}`, 'Choose a month or year', 'Previous month', 'Next month')
      + `<div class="wl-cal__week" aria-hidden="true">${WEEKDAYS.map((d) => `<span>${d}</span>`).join('')}</div>`
      + `<div class="wl-cal__grid">${grid}</div>`
      + '<div class="wl-cal__foot"><button type="button" class="wl-cal__today" data-cal-today>Today</button></div>';
  }

  function monthsHtml(): string {
    const year = cursor.getFullYear();
    const now = new Date();
    const selected = parseDateInput(input.value);
    const selYear = selected ? Number(selected.slice(0, 4)) : undefined;
    const selMonth = selected ? Number(selected.slice(5, 7)) - 1 : undefined;

    const cells = MONTHS.map((name, index) => {
      const classes = ['wl-cal__cell'];
      if (year === selYear && index === selMonth) classes.push('is-selected');
      if (year === now.getFullYear() && index === now.getMonth()) classes.push('is-today');
      return `<button type="button" class="${classes.join(' ')}" data-cal-month="${index}"`
        + ` tabindex="${index === cursor.getMonth() ? 0 : -1}">${escapeHtml(name.slice(0, 3))}</button>`;
    }).join('');

    return headHtml(String(year), 'Choose a year', 'Previous year', 'Next year')
      + `<div class="wl-cal__grid wl-cal__grid--wide">${cells}</div>`;
  }

  function yearsHtml(): string {
    const first = decadeStart(cursor.getFullYear());
    const now = new Date().getFullYear();
    const selected = parseDateInput(input.value);
    const selYear = selected ? Number(selected.slice(0, 4)) : undefined;

    let cells = '';
    for (let year = first; year < first + 12; year += 1) {
      const classes = ['wl-cal__cell'];
      if (year === selYear) classes.push('is-selected');
      if (year === now) classes.push('is-today');
      cells += `<button type="button" class="${classes.join(' ')}" data-cal-year="${year}"`
        + ` tabindex="${year === cursor.getFullYear() ? 0 : -1}">${year}</button>`;
    }

    // No zoom out past years: a century grid is a worse way to reach 1987
    // than typing it, and typing is right there.
    return `
      <div class="wl-cal__head">
        <button type="button" class="wl-cal__nav" data-cal-prev aria-label="Earlier years">&#8249;</button>
        <strong class="wl-cal__title wl-cal__title--flat">${first}&#8211;${first + 11}</strong>
        <button type="button" class="wl-cal__nav" data-cal-next aria-label="Later years">&#8250;</button>
      </div>
      <div class="wl-cal__grid wl-cal__grid--wide">${cells}</div>`;
  }

  const onPanelClick = (event: Event): void => {
    // Only OUR panel. These listeners are on the document, so a page with a
    // From and a To field has two of them: without this, clicking a day in
    // one calendar committed that date to both.
    if (!panel || !panel.contains(event.target as Node)) return;
    const target = event.target as HTMLElement;
    const day = target.closest<HTMLElement>('[data-cal-day]');
    if (day) { commit(day.dataset['calDay']!); close(); button.focus(); return; }

    // Picking a month or a year zooms back IN rather than committing. Only a
    // day is a date; a month on its own would have to invent one.
    const month = target.closest<HTMLElement>('[data-cal-month]');
    if (month) {
      cursor = new Date(cursor.getFullYear(), Number(month.dataset['calMonth']), 1);
      view = 'days';
      repaint();
      return;
    }
    const year = target.closest<HTMLElement>('[data-cal-year]');
    if (year) {
      cursor = new Date(Number(year.dataset['calYear']), cursor.getMonth(), 1);
      view = 'months';
      repaint();
      return;
    }

    if (target.closest('[data-cal-zoom]')) {
      view = view === 'days' ? 'months' : 'years';
      repaint();
      return;
    }
    if (target.closest('[data-cal-prev]')) { step(-1); return; }
    if (target.closest('[data-cal-next]')) { step(1); return; }
    if (target.closest('[data-cal-today]')) { commit(isoOf(new Date())); close(); button.focus(); }
  };

  /** Re-render and keep the focus inside the panel, so the keyboard does not fall out of it. */
  function repaint(): void {
    render();
    place();
    panel?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
  }

  /** What the arrows either side of the title move, which depends on the view. */
  function step(direction: number): void {
    if (view === 'days') cursor.setMonth(cursor.getMonth() + direction);
    else if (view === 'months') cursor.setFullYear(cursor.getFullYear() + direction);
    else cursor.setFullYear(cursor.getFullYear() + direction * 12);
    repaint();
  }

  const onPanelKey = (event: KeyboardEvent): void => {
    if (!panel) return;
    // Escape closes wherever the focus is — that is what Escape means over an
    // open popover, and focus may still be in the input that opened it.
    if (event.key === 'Escape') {
      // Escape zooms back in before it closes: having gone out to pick a
      // year, the way back is the way you came.
      if (view !== 'days') { view = view === 'years' ? 'months' : 'days'; repaint(); event.stopPropagation(); return; }
      close(); button.focus(); event.stopPropagation(); return;
    }
    // The rest is navigation WITHIN this calendar, so it has to be ours. With
    // a From and a To field on one page these listeners both see every key,
    // and without this the two calendars would move together.
    if (!panel.contains(event.target as Node)) return;

    if (view !== 'days') {
      // Four across in the month and year grids, seven in the day one — so
      // up and down have to know which grid they are in or they jump a row
      // and a half.
      const move = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -4, ArrowDown: 4 }[event.key];
      if (move === undefined) return;
      const cells = [...panel.querySelectorAll<HTMLElement>('[data-cal-month],[data-cal-year]')];
      const at = cells.indexOf((event.target as HTMLElement).closest<HTMLElement>('[data-cal-month],[data-cal-year]')!);
      const next = cells[Math.min(Math.max(at + move, 0), cells.length - 1)];
      if (next) { cells.forEach((c) => { c.tabIndex = -1; }); next.tabIndex = 0; next.focus(); }
      event.preventDefault();
      return;
    }

    const days = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
    if (days === undefined) return;
    const focused = (event.target as HTMLElement).closest<HTMLElement>('[data-cal-day]');
    if (!focused) return;
    const date = fromIso(focused.dataset['calDay']!);
    date.setDate(date.getDate() + days);
    cursor = new Date(date);
    render();
    place();
    const next = panel?.querySelector<HTMLElement>(`[data-cal-day="${isoOf(date)}"]`);
    if (next) { next.tabIndex = 0; next.focus(); }
    event.preventDefault();
  };

  const onDocPointer = (event: Event): void => {
    if (!panel) return;
    const target = event.target as Node;
    if (panel.contains(target) || button.contains(target)) return;
    close();
  };

  const onButtonClick = (): void => open();
  const onReposition = (): void => place();

  input.addEventListener('blur', onBlur);
  input.addEventListener('keydown', onKeyDown);
  button.addEventListener('click', onButtonClick);
  document.addEventListener('mousedown', onDocPointer, true);
  window.addEventListener('resize', onReposition);
  // Delegated on the document, because the panel is replaced on every
  // repaint and re-binding it each time is how a listener gets forgotten.
  document.addEventListener('click', onPanelClick, true);
  document.addEventListener('keydown', onPanelKey, true);

  return {
    dispose(): void {
      close();
      input.removeEventListener('blur', onBlur);
      input.removeEventListener('keydown', onKeyDown);
      button.removeEventListener('click', onButtonClick);
      document.removeEventListener('mousedown', onDocPointer, true);
      window.removeEventListener('resize', onReposition);
      document.removeEventListener('click', onPanelClick, true);
      document.removeEventListener('keydown', onPanelKey, true);
    },
  };
}

/** ISO to a LOCAL date — `new Date('2026-09-29')` is UTC midnight and can be the day before. */
function fromIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, month! - 1, day ?? 1);
}
